import React, { useState, useEffect, useMemo } from 'react';
import { Video, CloudSun, Umbrella, Waves } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import { api } from '@/lib/api';
import { WeatherBriefingCard } from '@/components/WeatherBriefingCard';
import { TenerifeMap } from '@/components/weather/TenerifeMap';
import { WebcamView } from '@/components/weather/WebcamView';
import { ForecastCard } from '@/components/weather/ForecastCard';
import { SpotsPanel } from '@/components/weather/SpotsPanel';
import { ShareButton } from '@/components/ShareButton';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import { distanceKm, camName } from '@/lib/tenerife';
import { conditionView, LEGEND, STATE_COLORS, COND_LABELS } from '@/lib/conditions';
import { AlertBand } from '@/components/AlertBand';

const T = {
  es: { webcam: 'Webcam', forecast: 'Previsión', beach: 'Playas', surf: 'Surf', source: 'Ver fuente oficial', empty: 'No hay webcams disponibles' },
  en: { webcam: 'Webcam', forecast: 'Forecast', beach: 'Beaches', surf: 'Surf', source: 'Official source', empty: 'No webcams available' },
  de: { webcam: 'Webcam', forecast: 'Vorhersage', beach: 'Strände', surf: 'Surfen', source: 'Offizielle Quelle', empty: 'Keine Webcams verfügbar' },
};
const MODE_KEY = 'weather_mode';

const WeatherPage = () => {
  const { language } = useLanguage();
  const { location, isRealLocation } = useLocation();
  const t = T[language] || T.es;
  const [cams, setCams] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState(() => new URLSearchParams(window.location.search).get('mode') || localStorage.getItem(MODE_KEY) || 'webcam');
  const deepSpot = useMemo(() => new URLSearchParams(window.location.search).get('spot'), []);
  const [loading, setLoading] = useState(true);

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const load = () => api.getWebcams().then((r) => setCams(r.data)).catch((e) => console.error('Error loading webcams:', e)).finally(() => setLoading(false));
    load();
    const refresh = setInterval(load, 5 * 60 * 1000);
    const tick = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, []);

  useEffect(() => {
    if (selectedId || cams.length === 0) return;
    const wanted = new URLSearchParams(window.location.search).get('cam');
    let pick = cams.find((c) => c.id === wanted) || cams.find((c) => c.name_es === 'Santa Cruz') || cams[0];
    if (!wanted && isRealLocation && location) {
      pick = cams.reduce((best, c) => (
        distanceKm(location.lat, location.lng, c.latitude, c.longitude) < distanceKm(location.lat, location.lng, best.latitude, best.longitude) ? c : best
      ), cams[0]);
    }
    setSelectedId(pick.id);
  }, [cams, location, isRealLocation, selectedId]);

  const selected = useMemo(() => cams.find((c) => c.id === selectedId) || null, [cams, selectedId]);
  const hasConditions = useMemo(() => cams.some((c) => conditionView(c.condition, now).state), [cams, now]);
  const selectedView = selected ? conditionView(selected.condition, now) : null;
  const CL = COND_LABELS[language] || COND_LABELS.es;

  const changeMode = (m) => { setMode(m); localStorage.setItem(MODE_KEY, m); };

  if (loading) {
    return (
      <div className="pb-20 max-w-3xl mx-auto px-4 py-4">
        <Skeleton className="w-full aspect-[1000/550] mb-4" />
        <Skeleton className="h-11 w-full mb-4" />
        <Skeleton className="h-[260px] w-full" />
      </div>
    );
  }

  return (
    <div className="pb-20 min-h-screen bg-gradient-to-b from-[#FDFBF7] to-white" data-testid="weather-page">
      <SEO
        title={language === 'en' ? 'Tenerife Weather & Live Webcams' : language === 'de' ? 'Teneriffa Wetter & Live-Webcams' : 'Tiempo en Tenerife y Webcams en Vivo'}
        description={language === 'en' ? 'Live webcams across Tenerife on one map, with local forecast: temperature, wind, waves and UV for each spot.' : language === 'de' ? 'Live-Webcams aus ganz Teneriffa auf einer Karte, mit lokaler Vorhersage: Temperatur, Wind, Wellen und UV.' : 'Webcams en directo de toda Tenerife en un solo mapa, con previsión local: temperatura, viento, olas y UV de cada punto.'}
        path="/weather"
        lang={language}
      />
      <div className="max-w-3xl mx-auto sm:px-6 py-2">
        <AlertBand type="tiempo" className="mb-3 px-4 sm:px-0" />

        {cams.length === 0 ? (
          <p className="px-4 py-10 text-center text-gray-500" data-testid="webcams-empty">{t.empty}</p>
        ) : (
          <>
            <TenerifeMap cams={cams} selectedId={selectedId} onSelect={(c) => setSelectedId(c.id)} language={language} now={now} />
            {hasConditions && (
              <p className="flex justify-center items-center gap-x-2.5 flex-nowrap overflow-hidden text-[11px] sm:text-xs text-gray-600 -mt-1 mb-1 px-3 whitespace-nowrap" data-testid="conditions-legend">
                {(LEGEND[language] || LEGEND.es).map(([k, label]) => (
                  <span key={k} className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: STATE_COLORS[k] }} aria-hidden="true" />{label}</span>
                ))}
              </p>
            )}
            {selected && (
              <p className="text-center -mt-0.5 mb-2" data-testid="selected-webcam-name">
                <span className="sm:hidden font-bold text-[#1D1D1F]">{camName(selected, language)}</span>
                {selectedView?.state && (
                  <span className="block text-[11px] text-gray-500" data-testid="selected-condition-updated">
                    {selected.condition?.texto_corto?.[language] || selected.condition?.texto_corto?.es ? `${selected.condition.texto_corto[language] || selected.condition.texto_corto.es} · ` : ''}{CL.updated} {selectedView.time}
                  </span>
                )}
              </p>
            )}

            <div className="px-4 sm:px-0 space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 grid grid-cols-4 bg-white shadow-md p-1" role="tablist" data-testid="weather-mode-switch">
                {[['webcam', Video, t.webcam], ['forecast', CloudSun, t.forecast], ['beach', Umbrella, t.beach], ['surf', Waves, t.surf]].map(([m, Icon, label]) => (
                  <button key={m} role="tab" aria-selected={mode === m} onClick={() => changeMode(m)}
                    className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2 sm:py-2.5 text-[11px] sm:text-sm font-semibold transition-colors duration-200 ${mode === m ? 'bg-[#38B2AC] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                    data-testid={`weather-mode-${m}`}>
                    <Icon size={17} /> {label}
                  </button>
                ))}
                </div>
                {selected && <ShareButton kind="w" itemId={selected.id} variant="ghost" className="bg-white shadow-md h-full py-3 px-3" testId="share-weather-btn" />}
              </div>

              {mode === 'webcam' && <WebcamView cam={selected} language={language} />}
              {mode === 'forecast' && <ForecastCard cam={selected} language={language} now={now} />}
              {(mode === 'beach' || mode === 'surf') && <SpotsPanel kind={mode} language={language} highlightId={deepSpot} />}
            </div>
          </>
        )}

        <div className="px-4 sm:px-0">
          <WeatherBriefingCard className="mt-6" />
        </div>
      </div>
    </div>
  );
};

export default WeatherPage;
